import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useParams } from 'react-router';
import { useStore } from '@/app/StoreContext';
import { api } from '@/api';
import { runtime } from '@/platform/runtime';
import { applyMainButton, hideMainButton } from '@/platform/mainButton';
import { fx } from '@/platform/feedback';
import { pluralize } from '@/utils/pluralize';
import { Button } from '@/components/ui/Button';
import type { Letters } from '@/domain/scoring';
import { roundHighlights } from '@/domain/roundHighlights';
import { LettersBanner } from './LettersBanner';
import { RoundHighlightCard } from './RoundHighlightCard';
import { StandingCard } from './StandingCard';
import styles from './RoundResultsView.module.css';

export const RoundResultsView = observer(function RoundResultsView() {
  const { match, room, auth } = useStore();
  const nav = useNavigate();
  const { code } = useParams<{ code: string }>();
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(false);
  const [restarting, setRestarting] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  const results = match.roundResults;
  const nextAt = match.nextRoundStartsAtUtc;
  const countdownSec = nextAt
    ? Math.max(0, Math.ceil((new Date(nextAt).getTime() - now) / 1000))
    : null;

  const isSolo = room.room?.settings.mode === 'Solo' || (results?.players?.length ?? 0) <= 1;

  const handlePlayAgain = async () => {
    if (restarting) return;
    setRestarting(true);
    try {
      const settings = room.room?.settings ?? {};
      if (code) await room.leave();
      const newRoom = await api.rooms.create(settings);
      fx.tap();
      nav(`/r/${newRoom.code}`);
    } catch {
      setRestarting(false);
    }
  };

  const handleLeave = async () => {
    if (code) {
      try { await room.leave(); } catch { /* ignore */ }
    }
    nav('/');
  };

  useEffect(() => {
    if (!runtime.isMiniApp) return;
    if (isSolo) {
      applyMainButton({
        text: 'Сыграть ещё',
        visible: true,
        active: !restarting,
        onClick: handlePlayAgain,
      });
    } else {
      applyMainButton({ text: 'Ожидание…', visible: true, active: false });
    }
    return () => hideMainButton();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSolo, restarting]);

  const lettersArr = useMemo<Letters | undefined>(() => {
    const l = results?.letters;
    if (!l || l.length < 3) return undefined;
    return [l[0], l[1], l[2]];
  }, [results?.letters]);

  const highlights = useMemo(
    () =>
      !isSolo && results?.players && lettersArr
        ? roundHighlights(results.players, results.letters ?? '')
        : null,
    [isSolo, results?.players, results?.letters, lettersArr],
  );

  const sortedPlayers = useMemo(
    () => [...(results?.players ?? [])].sort((a, b) => b.totalScore - a.totalScore),
    [results?.players],
  );

  const myPlayerId = auth.playerId ?? '';
  const myPlayerData = results?.players?.find(p => p.playerId === myPlayerId);
  const myHasZeroWords = (myPlayerData?.words?.length ?? 0) === 0;

  if (!results?.roundFinished) {
    return <p className={styles.waiting}>Считаем результаты…</p>;
  }

  if (dismissed) {
    return <p className={styles.waiting}>Ждём следующий раунд…</p>;
  }

  const header = (
    <div className={styles.header}>
      <span className={`kicker ${styles.kicker}`}>
        {isSolo ? 'Итог тренировки' : 'Итоги раунда'}
      </span>
      {lettersArr && (
        <div className={styles.bannerWrap}>
          <LettersBanner letters={lettersArr} size="sm" />
        </div>
      )}
    </div>
  );

  if (isSolo) {
    const myPlayer = results.players?.find(p => p.playerId === myPlayerId);
    return (
      <div className={styles.container}>
        {header}
        {myHasZeroWords && (
          <p className={styles.zeroBanner}>В этот раз — ноль слов</p>
        )}
        <div className={styles.playerList}>
          {myPlayer && (
            <StandingCard
              rank={1}
              playerId={myPlayer.playerId}
              nickname={myPlayer.nickname}
              totalScore={myPlayer.totalScore}
              words={myPlayer.words}
              letters={lettersArr}
              isMe
              delay={0.1}
              showUnique={false}
              noWordsCaption={myHasZeroWords ? '— ни одного слова за раунд' : undefined}
            />
          )}
        </div>
        <div className={styles.soloFooter}>
          <Button variant="primary" onClick={handlePlayAgain} disabled={restarting}>
            {restarting ? '…' : 'Сыграть ещё'}
          </Button>
          <Button variant="outline" onClick={handleLeave}>
            Выйти в главную
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {header}

      {myHasZeroWords && (
        <p className={styles.zeroBanner}>В этот раз — ноль слов</p>
      )}

      {highlights && lettersArr && (
        <div className={styles.highlights}>
          {highlights.longest && (
            <RoundHighlightCard
              tag="Самое длинное"
              word={highlights.longest.word}
              who={`нашёл(ла) ${highlights.longest.nickname}`}
              icon="↔"
              color="l2"
              letters={lettersArr}
              delay={0}
            />
          )}
          {highlights.rarest && (
            <RoundHighlightCard
              tag="Самое редкое"
              word={highlights.rarest.word}
              who={`${highlights.rarest.nickname} — единственный(ая)`}
              icon="✦"
              color="l3"
              letters={lettersArr}
              delay={0.12}
            />
          )}
          {highlights.overlap && (
            <RoundHighlightCard
              tag="Совпадение"
              word={highlights.overlap.word}
              who={`нашли ${pluralize(highlights.overlap.sharedBy ?? 2, 'игрок', 'игрока', 'игроков')}`}
              icon="⇄"
              color="l1"
              letters={lettersArr}
              delay={0.24}
            />
          )}
        </div>
      )}

      <div className={styles.playerList}>
        {sortedPlayers.map((p, i) => {
          const isMe = p.playerId === myPlayerId;
          return (
            <StandingCard
              key={p.playerId}
              rank={i + 1}
              playerId={p.playerId}
              nickname={p.nickname}
              totalScore={p.totalScore}
              words={p.words}
              letters={lettersArr}
              isMe={isMe}
              delay={0.36 + i * 0.1}
              noWordsCaption={isMe && myHasZeroWords ? '— ни одного слова за раунд' : undefined}
            />
          );
        })}
      </div>

      <div className={styles.matchFooter}>
        {countdownSec !== null ? (
          <>
            <p className={styles.countdown}>
              Следующий раунд через {pluralize(countdownSec, 'секунду', 'секунды', 'секунд')}
            </p>
            <Button variant="ghost" onClick={() => setDismissed(true)}>
              Пропустить →
            </Button>
          </>
        ) : (
          <p className={styles.countdown}>Подводим итоги матча…</p>
        )}
      </div>
    </div>
  );
});
